import { queryOptions } from "@tanstack/solid-query";
import { getNodeDefinitionInLocalOrRemote, getNodeLibrary } from "~/js/graph/node-library";
import AirtableMLBase from "~/js/network/airtable-ml-base";
import { SocketDefinition } from "~/js/nodes/node-definition";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { queryClient } from "~/solidJs/shared/api/queryClient";
import { fetchNeuralnetsOptions } from "../nodes/neural-networks";

const MergeMlBaseKey = "key_MergeMlBaseKey";

/**
 *
 * @param {import("~/solidJs/shared/helpers/types").UserSubscriptionPlanEnum | undefined} userGroup
 * @returns
 */
export const getMergeMlBaseOptions = (userGroup = getUserStore()?.group) =>
	queryOptions({
		queryKey: [MergeMlBaseKey, userGroup],
		queryFn: merge,
		gcTime: Number.POSITIVE_INFINITY
	});

/**
 * perform the merge data from backend and airtable, and put it in cache
 */
export const mergeMlBase = async () => {
	return queryClient.fetchQuery(getMergeMlBaseOptions(getUserStore().group));
};

/**
 * gets data from cache or fetch from backend and airtable then perform merge
 */
const merge = async () => {
	const [airtableList] = await Promise.all([AirtableMLBase.get(), fetchNeuralnetsOptions()]);
	for (let i = 0; i < airtableList.length; i++) {
		const item = airtableList[i];
		const nodeForItem = getNodeDefinitionInLocalOrRemote(item.globalId);
		if (nodeForItem) {
			// we need to save promise for airtable item to later acces it in sidebar
			item.promise = nodeForItem.promise;
			mergeNodeWithAirtableItem(item, nodeForItem.definition);
		}

		const customElementItem = getNodeLibrary().customObjectsLibrary[item.globalId];
		if (customElementItem) {
			item.isCustomElement = true;
			item.node = customElementItem;
			customElementItem.name = item.name;
			customElementItem.description = item.description;
			customElementItem.category = item.category;
			customElementItem.coverPictureUrl = item.coverPictureUrl;
			customElementItem.tags = item.tags;
			customElementItem.neuralNetworks = item.neuralNetworks;
		}
	}

	return airtableList;
};
/**
 * @description merge node and socket definition with data from airtable
 * @param {import('~/js/network/airtable-ml-base').IMlBaseItem} item
 * @param {import("../nodes/node-definition").NodeDefinition} nodeForItem
 */
export const mergeNodeWithAirtableItem = (item, nodeForItem) => {
	item.node = nodeForItem;
	nodeForItem.name = item.name;
	nodeForItem.description = item.description;
	nodeForItem.category = item.category;
	nodeForItem.coverPictureUrl = item.coverPictureUrl;
	nodeForItem.tags = item.tags;
	nodeForItem.neuralNetworks = item.neuralNetworks;
	nodeForItem.stickerLearnMoreUrl = item.stickerLearnMoreUrl;
	nodeForItem.selectedPremiumPlan = item.selectedPremiumPlan;
	nodeForItem.premiumBannerText = item.premiumBannerText;
	nodeForItem.disabledBanner = item.disabledBanner;
	// hover flags
	nodeForItem.isNeedToFetchModels = item.isNeedToFetchModels;
	nodeForItem.isNeedToFetchLoras = item.isNeedToFetchLoras;
	nodeForItem.isNeedToSvg = item.isNeedToSvg;
	nodeForItem.isNeedToGenPrompt = item.isNeedToGenPrompt;
	nodeForItem.isNeedToOutPaint = item.isNeedToOutPaint;
	nodeForItem.isNeedToZoomOut = item.isNeedToZoomOut;
	nodeForItem.isNeedToVariate = item.isNeedToVariate;
	nodeForItem.isNeedToSynchOutputs = item.isNeedToSynchOutputs;
	nodeForItem.isNeedToAutoMask = item.isNeedToAutoMask;
	nodeForItem.isNeedToPromptEnhance = item.isNeedToPromptEnhance;
	nodeForItem.isNeedToDescribeImage = item.isNeedToDescribeImage;
	nodeForItem.isNeedToMjVariate = item.isNeedToMjVariate;
	nodeForItem.isNeedToMjUpscale = item.isNeedToMjUpscale;
	nodeForItem.isNeedToMjZoomOut = item.isNeedToMjZoomOut;
	nodeForItem.isNeedToMjPan = item.isNeedToMjPan;
	nodeForItem.isNeedToRevertSocketOrder = item.isNeedToRevertSocketOrder;
	nodeForItem.isNeedToVariationsPro = item.isNeedToVariationsPro;
	nodeForItem.isNeedToPromptVariations = item.isNeedToPromptVariations;
	nodeForItem.isNeedToReimagine = item.isNeedToReimagine;
	nodeForItem.isNeedToRecolor = item.isNeedToRecolor;
	nodeForItem.isNeedToRestyle = item.isNeedToRestyle;
	nodeForItem.isNeedToCrop = item.isNeedToCrop;
	nodeForItem.isNeedToVision = item.isNeedToVision;
	nodeForItem.isNeedToRandomModels = item.isNeedToRandomModels;
	nodeForItem.isNeedDalleGenerate = item.isNeedDalleGenerate;
	nodeForItem.isNeedToDetailedUpscale = item.isNeedToDetailedUpscale;
	nodeForItem.isNeedToUpscale = item.isNeedToUpscale;
	nodeForItem.isNeedToFaceRestore = item.isNeedToFaceRestore;
	nodeForItem.isNeedToDownscale = item.isNeedToDownscale;
	nodeForItem.isEnglishOnly = item.isEnglishOnly;

	nodeForItem.disabledPremiumText = item.disabledPremiumText;
	nodeForItem.searchTags = item.searchTags;
	nodeForItem.isPremium = item.isPremium;
	nodeForItem.instructionText = item.instructionText;
	nodeForItem.instructionGif = item.instructionGif;
	nodeForItem.medianTime = item.medianTime;
	// legacy, do not configure custom socket by boolean flag
	if (item.promptStyles) {
		try {
			const promptStyles = JSON.parse(item.promptStyles);

			const promptStylesSocket = new SocketDefinition(
				"prompt_styles",
				"enum",
				{
					group: promptStyles.group || "Dreambooth",
					value: promptStyles.values[0].value,
					valueOptions: { values: promptStyles.values },
					globalCheckedModifiers: promptStyles.globalCheckedModifiers,
					globalUnCheckedModifiers: promptStyles.globalUnCheckedModifiers,
					isNeedToMoveFromSettings: promptStyles.isNeedToMoveFromSettings
				},
				true
			);
			nodeForItem.params.push(promptStylesSocket);
		} catch (e) {
			console.log(e);
		}
	}
	// legacy, do not configure custom socket by boolean flag
	if (item.isNeedToAddStyle) {
		try {
			const styleSocket = new SocketDefinition(
				"style",
				"modifier",
				{
					group: "Dreambooth",
					isNeedToMoveFromSettings: true,
					isNeedToHideName: true
				},
				true,
				"Style"
			);
			nodeForItem.params.push(styleSocket);
		} catch (e) {
			console.log(e);
		}
	}
	// legacy, do not configure custom socket by boolean flag
	if (nodeForItem.globalId?.includes?.("dreambooth")) {
		try {
			const uniqueNameSocket = new SocketDefinition(
				"unique_name",
				"text",
				{
					group: "Dreambooth",

					isNeedToMoveFromSettings: true
				},
				false
			);
			nodeForItem.inputs.unshift(uniqueNameSocket);
		} catch (e) {
			console.log(e);
		}
	}
	nodeForItem.setSocketsDisplay(item.socketsDisplay);
	if (item.socketsToModify) {
		modifyMlDefinition(nodeForItem, item.socketsToModify);
	}
};

/**
 * merges data from backend with data from airtable, differs from {@link NodeDefinition.setSocketsDisplay}, can change order of sockets, create new sockets
 * use this function for all new merge functionality
 * @argument definition {import("~/js/nodes/node-definition").NodeDefinition}
 * @argument socketsToModify {string} JSON with data to modify
 */
export const modifyMlDefinition = (definition, socketsToModify) => {
	try {
		const modifyData = JSON.parse(socketsToModify);

		// Create a new ordered array for params and inputs
		const newParams = [];
		const newInputs = [];
		const newOutputs = [];

		// Create maps to track which sockets are being modified
		const paramsMap = new Map();
		const inputsMap = new Map();
		const outputsMap = new Map();

		const modifiedSockets = new Set();

		// Map all existing sockets for quick lookup
		for (const socket of definition.params) {
			paramsMap.set(socket.name, socket);
		}
		for (const socket of definition.inputs) {
			inputsMap.set(socket.name, socket);
		}
		for (const socket of definition.outputs) {
			outputsMap.set(socket.name, socket);
		}

		// Process all socket modifications in order and add to new arrays
		for (const newSocketData of modifyData) {
			const name = newSocketData.name;

			// Find or create socket
			let socket = inputsMap.get(name) || outputsMap.get(name) || paramsMap.get(name);
			let isInput = inputsMap.has(name);
			let isOutput = outputsMap.has(name);
			if (!socket) {
				// Create new socket
				socket = createNewSocket(newSocketData);
				if (newSocketData.type === "input") {
					isInput = true;
					inputsMap.set(name, socket);
				} else if (newSocketData.type === "output") {
					isOutput = true;
					outputsMap.set(name, socket);
				} else {
					paramsMap.set(name, socket);
				}
			}

			// Update socket properties
			updateSocketProperties(socket, newSocketData);
			if (modifiedSockets.has(name)) {
				continue;
			}
			modifiedSockets.add(name);
			// Add modified socket to the appropriate new array
			// and remove from maps to prevent duplicates
			if (isInput) {
				newInputs.push(socket);
			} else if (isOutput) {
				newOutputs.push(socket);
			} else {
				newParams.push(socket);
			}
		}
		for (const originalSocket of definition.params) {
			if (modifiedSockets.has(originalSocket.name)) {
				continue;
			}
			newParams.push(originalSocket);
		}
		for (const originalSocket of definition.inputs) {
			if (modifiedSockets.has(originalSocket.name)) {
				continue;
			}
			newInputs.push(originalSocket);
		}
		for (const originalSocket of definition.outputs) {
			if (modifiedSockets.has(originalSocket.name)) {
				continue;
			}
			newOutputs.push(originalSocket);
		}
		// Replace the original arrays with new ordered ones
		definition.params = newParams;
		definition.inputs = newInputs;
		definition.outputs = newOutputs;
	} catch (e) {
		console.error(e);
	}
};

/**
 * Creates a new socket definition
 * @param {Object} socketData - Data for the socket
 * @returns {SocketDefinition} The newly created socket
 */
const createNewSocket = (socketData) => {
	return new SocketDefinition(
		socketData.name,
		socketData.dataType || "enum",
		{
			group: socketData.group || "Dreambooth",
			value: socketData.value,
			valueOptions: { values: socketData.values },
			globalCheckedModifiers: socketData.globalCheckedModifiers,
			globalUnCheckedModifiers: socketData.globalUnCheckedModifiers,
			isNeedToMoveFromSettings: socketData.isNeedToMoveFromSettings
		},
		socketData.optional,
		socketData.displayName
	);
};

/**
 * @description Updates socket properties based on provided data, same as
 * @param {import("../nodes/node-definition").SocketDefinition} socket - The socket to update
 * @param {Object} socketData - Data containing properties to update
 */
const updateSocketProperties = (socket, socketData) => {
	// Ensure valueOptions exists
	if (!socket?.optionalInfo?.valueOptions) {
		socket.optionalInfo.valueOptions = {};
	}

	if (socketData.value !== undefined) {
		socket.value = socketData.value;
		socket.optionalInfo.value = socketData.value;
	}

	if (socketData.group !== undefined) {
		socket.optionalInfo.group = socketData.group;
	}

	if (socketData.values !== undefined) {
		socket.optionalInfo.valueOptions.values = socketData.values;
	}
	if (socketData.dataType !== undefined) {
		socket.dataType = socketData.dataType;
	}
	if (socketData.placeholder !== undefined) {
		socket.optionalInfo.placeholder = socketData.placeholder;
	}
	if (socketData.globalCheckedModifiers !== undefined) {
		socket.optionalInfo.globalCheckedModifiers = socketData.globalCheckedModifiers;
	}
	if (socketData.customErrorMessages) {
		socket.optionalInfo.customErrorMessages = socketData.customErrorMessages;
	}
	if (socketData.globalUnCheckedModifiers !== undefined) {
		socket.optionalInfo.globalUnCheckedModifiers = socketData.globalUnCheckedModifiers;
	}
	if (socketData.isNeedToMoveFromSettings !== undefined) {
		socket.optionalInfo.isNeedToMoveFromSettings = socketData.isNeedToMoveFromSettings;
	}
	if (socketData.isNeedToFetchLoras !== undefined) {
		socket.optionalInfo.isNeedToFetchLoras = socketData.isNeedToFetchLoras;
	}
	if (socketData.limits !== undefined) {
		socket.optionalInfo.valueOptions.limits = socketData.limits;
	}
	if (socketData.displayName !== undefined) {
		socket.setDisplayName(socketData.displayName);
	}
	if (socketData.tariffLimits !== undefined) {
		socket.optionalInfo.valueOptions.tariffLimits = socketData.tariffLimits;
	}
	if (socketData.additionalLabel !== undefined) {
		socket.optionalInfo.additionalLabel = socketData.additionalLabel;
	}
	if (socketData.validateSimilar) {
		socket.optionalInfo.valueOptions.validateSimilar = socketData.validateSimilar;
	}

	if (socketData.isHandleHidden) {
		socket.optionalInfo.isHandleHidden = true;
	}
};
